import { StanButton, StanModal } from '@/components';
import type { RoutingWarning } from '../../../logic';

export type { RoutingWarning };

interface RoutingWarningModalProps {
    isOpen: boolean;
    warnings: RoutingWarning[];
    isPublishing: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

/**
 * Soft-warn confirmation shown before publishing when the page's routing
 * would introduce a cycle or orphan another step. The user can still choose
 * to save — we only nudge.
 */
export const RoutingWarningModal = ({
    isOpen,
    warnings,
    isPublishing,
    onClose,
    onConfirm,
}: RoutingWarningModalProps) => {
    return (
        <StanModal
            id='funnel-routing-warning-modal'
            size='sm'
            isOpen={isOpen}
            onClose={onClose}
            title='Check your routing'
        >
            <div className='flex flex-col gap-4'>
                <p className='text-body-md text-gray'>
                    Saving this page will introduce the following routing issue
                    {warnings.length > 1 ? 's' : ''}:
                </p>
                <ul className='flex flex-col gap-2'>
                    {warnings.map(w => (
                        <li
                            key={w.id}
                            className='border-neutral-subtle bg-canvas-subtle text-body-sm text-dark rounded-md border p-3'
                        >
                            <span className='font-medium'>
                                {w.kind === 'cycle' ? 'Routing loop: ' : 'Unreachable page: '}
                            </span>
                            {w.message}
                        </li>
                    ))}
                </ul>
                <p className='text-body-sm text-gray'>
                    You can still save, but customers may get stuck or miss steps until you fix this.
                </p>
                <div className='flex w-full gap-1 pt-2 max-sm:flex-col sm:gap-2'>
                    <StanButton
                        name='cancel-routing-warning'
                        variant='outline'
                        onClick={onClose}
                        className='w-full'
                        disabled={isPublishing}
                    >
                        Go Back
                    </StanButton>
                    <StanButton
                        name='confirm-routing-warning'
                        onClick={onConfirm}
                        disabled={isPublishing}
                        loading={isPublishing}
                        className='w-full'
                    >
                        Save Anyway
                    </StanButton>
                </div>
            </div>
        </StanModal>
    );
};
